import { advertisingAge, advertisingPageFamily } from './advertisingPolicy.js'
import { acquisitionSource, isSensitiveAnalyticsRoute, publicAnalyticsContext } from './seoAnalyticsContext.js'
const MEASUREMENT_ID = 'G-EJT9RMRPLB'
const SOURCE_STORAGE_KEY = 'visualg:seo-source:v1'
const sources = new Set(['chatgpt', 'perplexity', 'claude', 'copilot', 'gemini', 'google_search', 'bing_search', 'other'])
const families = new Set(['editor', 'nexo', 'learning', 'seo_download'])
const funnelEvents = new Set(['seo_cta_clicked', 'editor_opened_from_landing', 'learning_topic_viewed', 'learning_example_run_started', 'learning_example_run_finished', 'code_run_started', 'code_run_finished'])
let lastPage = null
let allowed = false
let source = null
let owner = null

const clearSource = () => {
  source = null
  try { sessionStorage.removeItem(SOURCE_STORAGE_KEY) } catch { /* Storage may be unavailable. */ }
}

const resolveSource = () => {
  const detected = acquisitionSource({ referrer: document.referrer, search: location.search, origin: location.origin })
  try {
    const cached = JSON.parse(sessionStorage.getItem(SOURCE_STORAGE_KEY) || 'null')
    const age = Date.now() - cached?.capturedAt
    if (detected === 'unknown' && sources.has(cached?.source) && age >= 0 && age < 30 * 60 * 1000) return cached.source
    sessionStorage.removeItem(SOURCE_STORAGE_KEY)
    if (sources.has(detected)) sessionStorage.setItem(SOURCE_STORAGE_KEY, JSON.stringify({ source: detected, capturedAt: Date.now() }))
  } catch { /* In-memory attribution still works without session storage. */ }
  return detected
}

export function trackGoogleAnalyticsEvent(eventName, properties = {}) {
  if (!allowed || !funnelEvents.has(eventName) || isSensitiveAnalyticsRoute(location.pathname) || window.__visualgSensitiveAuthPage || typeof window.gtag !== 'function') return false
  const parameters = {
    ...publicAnalyticsContext(location.pathname, properties), acquisition_source: source || 'unknown',
    page_location: location.origin + '/' + advertisingPageFamily(location.pathname),
    page_referrer: '', page_title: advertisingPageFamily(location.pathname), send_to: MEASUREMENT_ID,
  }
  if (['completed', 'failed', 'stopped', 'blocked'].includes(properties.status)) parameters.run_status = properties.status
  window.gtag('event', eventName, parameters)
  return true
}

export function syncGoogleAnalytics({ consent, user, sensitive, pageFamily, pathname = location.pathname }) {
  const age = advertisingAge(user)
  const nextOwner = user?.profile_uuid || user?.uuid || user?.id || null
  if (owner !== nextOwner) {
    if (owner !== null) clearSource()
    source = null; lastPage = null; owner = nextOwner
  }
  allowed = !sensitive && !window.__visualgSensitiveAuthPage && !isSensitiveAnalyticsRoute(pathname) && consent?.analytics === 'granted' && age !== null && age >= 18
  window['ga-disable-' + MEASUREMENT_ID] = !allowed
  if (!allowed) {
    lastPage = null
    source = null
    // Auth loading does not read or collect attribution. Revocation and known ineligible users clear it.
    if (sensitive || isSensitiveAnalyticsRoute(pathname) || consent?.analytics !== 'granted' || user !== null && user !== undefined) clearSource()
    window.gtag?.('consent', 'update', { analytics_storage: 'denied', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' })
    return
  }
  if (source === null) source = resolveSource()
  const family = families.has(pageFamily) ? pageFamily : 'other'
  const editorial = publicAnalyticsContext(pathname)
  window.dataLayer = window.dataLayer || []
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments) }
  if (!document.querySelector('script[data-visualg-ga]')) {
    window.gtag('consent', 'default', { analytics_storage: 'granted', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' })
    window.gtag('js', new Date())
    window.gtag('config', MEASUREMENT_ID, { send_page_view: false, page_location: location.origin + '/' + family, page_referrer: '', allow_google_signals: false, allow_ad_personalization_signals: false })
    const script = document.createElement('script')
    script.async = true
    script.dataset.visualgGa = 'true'
    script.src = 'https://www.googletagmanager.com/gtag/js?id=' + MEASUREMENT_ID
    if (window.happyDOM?.settings?.disableJavaScriptFileLoading) script.type = 'text/plain'
    document.head.appendChild(script)
  }
  window.gtag('consent', 'update', { analytics_storage: 'granted', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' })
  // Only coarse families: no URL, queries, account names, code or DOB.
  const pageKey = `${family}:${editorial.content_id}:${editorial.language}`
  if (lastPage !== pageKey) {
    lastPage = pageKey
    window.gtag('event', 'page_view', { page_location: location.origin + '/' + family, page_referrer: '', page_title: family, page_family: family, ...editorial, acquisition_source: source })
  }
}
